import React, { useRef, useEffect } from 'react';

interface MatrixBackgroundProps {
  density?: number;
  fontSize?: number;
  speed?: number;
  color?: string;
  backgroundColor?: string;
  className?: string;
  fadeLength?: number;
  characters?: string;
}

const MatrixBackground: React.FC<MatrixBackgroundProps> = ({
  density = 0.08,
  fontSize = 14, 
  speed = 2,
  color = '#00ff00',
  backgroundColor = 'rgba(0, 0, 0, 0.85)',
  className = '',
  fadeLength = 20,
  characters = 'ｱｲｳｴｵｶｷｸｹｺｻｼｽｾｿﾀﾁﾂﾃﾄﾅﾆﾇﾈﾉﾊﾋﾌﾍﾎﾏﾐﾑﾒﾓﾔﾕﾖﾗﾘﾙﾚﾛﾜﾝ0123456789'
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const requestRef = useRef<number | undefined>(undefined);
  const columns = useRef<number[]>([]);
  const resizeObserverRef = useRef<ResizeObserver | undefined>(undefined);
  
  // Initialize the columns for the matrix rain
  const initializeColumns = (width: number) => {
    // Calculate number of columns based on canvas width and fontSize
    const columnCount = Math.floor(width / fontSize);
    const newColumns = [];
    
    // Set initial y position for each column (all off-screen at start)
    for (let i = 0; i < columnCount; i++) {
      newColumns[i] = -Math.random() * 100;
    }
    
    columns.current = newColumns;
  };
  
  // Animation loop
  const animate = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Create a semi-transparent background for the trail effect
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Set the text style
    ctx.fillStyle = color;
    ctx.font = `${fontSize}px monospace`;
    
    // Draw the symbols
    for (let i = 0; i < columns.current.length; i++) {
      // Get a random character from our character set
      const randomChar = characters.charAt(Math.floor(Math.random() * characters.length));
      const x = i * fontSize;
      const y = columns.current[i] * fontSize;
      
      // Draw the character with gradient effect for the trail
      for (let j = 0; j < fadeLength; j++) {
        // Calculate different opacity levels for the trail
        const opacity = 1 - (j / fadeLength);
        const trailY = y - (j * fontSize);
        
        if (trailY < 0) continue;
        
        // Sometimes skip drawing to create more randomness
        if (Math.random() > 0.98) continue;
        
        // Draw character with varying opacity
        // Convert any color format to rgba with the calculated opacity
        if (color.startsWith('#')) {
          // Hex color format - convert to rgba
          const hex = color.slice(1);
          const r = parseInt(hex.length === 3 ? hex[0] + hex[0] : hex.slice(0, 2), 16);
          const g = parseInt(hex.length === 3 ? hex[1] + hex[1] : hex.slice(2, 4), 16);
          const b = parseInt(hex.length === 3 ? hex[2] + hex[2] : hex.slice(4, 6), 16);
          ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
        } else if (color.startsWith('rgb')) {
          // RGB or RGBA format - extract values and replace opacity
          ctx.fillStyle = color.replace(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/, 
            `rgba($1, $2, $3, ${opacity})`);
        } else {
          // For other color formats (named colors, etc)
          ctx.fillStyle = color; // Use original color but with reduced opacity
          ctx.globalAlpha = opacity;
        }
        
        // Occasionally use a brighter white character for variety
        if (j === 0 && Math.random() < 0.04) {
          ctx.fillStyle = `rgba(255, 255, 255, ${opacity})`;
          ctx.globalAlpha = 1.0; // Reset globalAlpha if we've set it
        }
        
        // Get a different character for each position in the trail
        const trailChar = characters.charAt(Math.floor(Math.random() * characters.length));
        ctx.fillText(j === 0 ? randomChar : trailChar, x, trailY);
        
        // Reset globalAlpha if we've modified it
        if (!color.startsWith('#') && !color.startsWith('rgb')) {
          ctx.globalAlpha = 1.0;
        }
      }
      
      // Update the y position for the next frame
      columns.current[i] += speed;
      
      // If the column has moved past the screen height, reset it
      if (columns.current[i] * fontSize > canvas.height && Math.random() > 0.975) {
        columns.current[i] = 0;
      }
      
      // Randomly create new columns with some probability
      if (columns.current[i] === 0 && Math.random() > 1 - density) {
        columns.current[i] = -Math.random() * 100;
      }
    }
    
    requestRef.current = requestAnimationFrame(animate);
  };
  
  // Set up and clean up
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    // Set initial canvas size
    canvas.width = canvas.clientWidth;
    canvas.height = canvas.clientHeight;
    
    // Initialize columns
    initializeColumns(canvas.width);
    
    // Start animation
    requestRef.current = requestAnimationFrame(animate);
    
    // Set up resize observer
    const resizeObserver = new ResizeObserver(() => {
      if (canvas) {
        // Update canvas dimensions
        canvas.width = canvas.clientWidth;
        canvas.height = canvas.clientHeight;
        
        // Reinitialize columns when resized
        initializeColumns(canvas.width);
      }
    });
    
    resizeObserver.observe(canvas);
    resizeObserverRef.current = resizeObserver;
    
    // Clean up
    return () => {
      if (requestRef.current) {
        cancelAnimationFrame(requestRef.current);
      }
      
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, []);
  
  return (
    <canvas 
      ref={canvasRef} 
      className={`background-canvas matrix-background ${className}`}
      style={{ 
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none'
      }}
    />
  );
};

export default MatrixBackground;